import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * WhatsApp/Instagram-style image editor — draw, plus EDITABLE text labels.
 * Plain <canvas>, no extra dependency (kept in step with the widget's editor).
 *
 * Architecture: text is NOT burned into the pixels. We keep a raster BASE layer
 * (photo + pen strokes) and a list of text OBJECTS composited on top every frame —
 * that's what makes a label re-editable (retype / resize / recolour / box / move /
 * delete) after it's placed.
 */
export interface ImageEditorProps {
  src: string
  onCancel: () => void
  onSave: (blob: Blob) => void
  saving?: boolean
}

interface TextItem {
  id: string
  x: number
  y: number
  text: string
  color: string
  size: number
  boxed: boolean
}

const COLORS = ['#FF4655', '#FFFFFF', '#111827', '#2563EB', '#16A34A', '#F59E0B']
const FONT = (size: number) => `bold ${size}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`

function contrastOn(hex: string): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#111827' : '#FFFFFF'
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2)
  g.beginPath()
  g.moveTo(x + rr, y)
  g.lineTo(x + w - rr, y)
  g.quadraticCurveTo(x + w, y, x + w, y + rr)
  g.lineTo(x + w, y + h - rr)
  g.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
  g.lineTo(x + rr, y + h)
  g.quadraticCurveTo(x, y + h, x, y + h - rr)
  g.lineTo(x, y + rr)
  g.quadraticCurveTo(x, y, x + rr, y)
  g.closePath()
}

export const ImageEditor: React.FC<ImageEditorProps> = ({ src, onCancel, onSave, saving = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const baseRef = useRef<HTMLCanvasElement | null>(null)
  const drawingRef = useRef(false)
  const dragRef = useRef<{ id: string; dx: number; dy: number } | null>(null)
  const historyRef = useRef<Array<{ base: ImageData; items: TextItem[] }>>([])
  const textInputRef = useRef<HTMLInputElement>(null)

  const [tool, setTool] = useState<'pen' | 'text'>('pen')
  const [color, setColor] = useState<string>(COLORS[0])
  const [items, setItems] = useState<TextItem[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  const selected = items.find((i) => i.id === selectedId) || null

  const ctx = () => canvasRef.current?.getContext('2d') || null
  const baseCtx = () => baseRef.current?.getContext('2d') || null

  const redraw = useCallback((list: TextItem[], selId: string | null) => {
    const c = canvasRef.current
    const g = ctx()
    const base = baseRef.current
    if (!c || !g || !base) return
    g.clearRect(0, 0, c.width, c.height)
    g.drawImage(base, 0, 0)

    for (const it of list) {
      g.font = FONT(it.size)
      g.textBaseline = 'top'
      const w = g.measureText(it.text || ' ').width
      const h = it.size * 1.25
      if (it.boxed) {
        const pad = it.size * 0.28
        g.fillStyle = it.color
        roundRect(g, it.x - pad, it.y - pad * 0.6, w + pad * 2, h + pad * 1.2, it.size * 0.28)
        g.fill()
        g.fillStyle = contrastOn(it.color)
      } else {
        // No outline/stroke — just the chosen colour, as requested.
        g.fillStyle = it.color
      }
      g.fillText(it.text, it.x, it.y)

      if (it.id === selId) {
        const pad = it.size * 0.3
        g.strokeStyle = 'rgba(37,99,235,0.95)'
        g.lineWidth = Math.max(1.5, it.size * 0.05)
        g.setLineDash([it.size * 0.25, it.size * 0.2])
        g.strokeRect(it.x - pad, it.y - pad * 0.6, w + pad * 2, h + pad * 1.2)
        g.setLineDash([])
      }
    }
  }, [])

  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      const c = canvasRef.current
      if (!c) return
      const MAX_W = 1400
      const scale = img.naturalWidth > MAX_W ? MAX_W / img.naturalWidth : 1
      c.width = Math.max(1, Math.round(img.naturalWidth * scale))
      c.height = Math.max(1, Math.round(img.naturalHeight * scale))

      const base = document.createElement('canvas')
      base.width = c.width
      base.height = c.height
      base.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height)
      baseRef.current = base

      historyRef.current = []
      setItems([])
      setSelectedId(null)
      setReady(true)
      redraw([], null)
    }
    img.src = src
  }, [src, redraw])

  useEffect(() => {
    if (ready) redraw(items, selectedId)
  }, [items, selectedId, ready, redraw])

  const pushHistory = (list: TextItem[]) => {
    const base = baseRef.current
    const bg = baseCtx()
    if (!base || !bg) return
    try {
      historyRef.current.push({
        base: bg.getImageData(0, 0, base.width, base.height),
        items: list.map((i) => ({ ...i })),
      })
      if (historyRef.current.length > 25) historyRef.current.shift()
    } catch {
      /* ignore */
    }
  }

  const undo = () => {
    const snap = historyRef.current.pop()
    const bg = baseCtx()
    if (!snap || !bg) return
    bg.putImageData(snap.base, 0, 0)
    setItems(snap.items)
    setSelectedId(null)
  }

  const toCanvas = (clientX: number, clientY: number) => {
    const c = canvasRef.current!
    const r = c.getBoundingClientRect()
    return { x: (clientX - r.left) * (c.width / r.width), y: (clientY - r.top) * (c.height / r.height) }
  }

  const hitTest = (p: { x: number; y: number }): TextItem | null => {
    const g = ctx()
    if (!g) return null
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i]
      g.font = FONT(it.size)
      const w = g.measureText(it.text || ' ').width
      const h = it.size * 1.25
      const pad = it.size * 0.3
      if (p.x >= it.x - pad && p.x <= it.x + w + pad && p.y >= it.y - pad && p.y <= it.y + h + pad) return it
    }
    return null
  }

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!ready) return
    const c = canvasRef.current!
    const p = toCanvas(e.clientX, e.clientY)

    if (tool === 'text') {
      e.preventDefault()
      const hit = hitTest(p)
      if (hit) {
        pushHistory(items)
        setSelectedId(hit.id)
        dragRef.current = { id: hit.id, dx: p.x - hit.x, dy: p.y - hit.y }
        try { c.setPointerCapture(e.pointerId) } catch { /* ignore */ }
        return
      }
      pushHistory(items)
      const size = Math.max(20, Math.round(c.width / 22))
      const item: TextItem = {
        id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        x: p.x, y: p.y, text: '', color, size, boxed: false,
      }
      setItems((prev) => [...prev, item])
      setSelectedId(item.id)
      setTimeout(() => textInputRef.current?.focus(), 0)
      return
    }

    const bg = baseCtx()
    if (!bg) return
    pushHistory(items)
    setSelectedId(null)
    drawingRef.current = true
    try { c.setPointerCapture(e.pointerId) } catch { /* ignore */ }
    bg.strokeStyle = color
    bg.lineWidth = Math.max(3, Math.round(c.width / 250))
    bg.lineCap = 'round'
    bg.lineJoin = 'round'
    bg.beginPath()
    bg.moveTo(p.x, p.y)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!ready) return
    const p = toCanvas(e.clientX, e.clientY)

    if (dragRef.current) {
      const d = dragRef.current
      setItems((prev) => prev.map((i) => (i.id === d.id ? { ...i, x: p.x - d.dx, y: p.y - d.dy } : i)))
      return
    }
    if (!drawingRef.current) return
    const bg = baseCtx()
    if (!bg) return
    bg.lineTo(p.x, p.y)
    bg.stroke()
    redraw(items, selectedId)
  }

  const onPointerUp = () => {
    drawingRef.current = false
    dragRef.current = null
  }

  const patch = (id: string, p: Partial<TextItem>) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...p } : i)))

  const removeSelected = () => {
    if (!selected) return
    pushHistory(items)
    setItems((prev) => prev.filter((i) => i.id !== selected.id))
    setSelectedId(null)
  }

  const done = () => {
    const c = canvasRef.current
    if (!c) return
    const clean = items.filter((i) => i.text.trim())
    setItems(clean)
    setSelectedId(null)
    redraw(clean, null)
    setTimeout(() => c.toBlob((blob) => { if (blob) onSave(blob) }, 'image/png'), 0)
  }

  const toolBtn = (active: boolean) =>
    `px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
      active
        ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
        : 'border-border bg-surface text-txt-primary hover:bg-surface-hover'
    }`

  // NOTE: no backdrop click-to-close — only Cancel / Done dismiss the editor.
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-3">
      <div className="bg-surface rounded-xl p-3 max-w-[96vw] max-h-[94vh] flex flex-col gap-2 overflow-auto">
        {/* Toolbar */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button type="button" className={toolBtn(tool === 'pen')} onClick={() => { setTool('pen'); setSelectedId(null) }}>
            ✏️ Draw
          </button>
          <button type="button" className={toolBtn(tool === 'text')} onClick={() => setTool('text')}>T Text</button>
          <div className="flex gap-1 ml-1">
            {COLORS.map((col) => (
              <button
                key={col}
                type="button"
                aria-label={`Colour ${col}`}
                onClick={() => { setColor(col); if (selected) patch(selected.id, { color: col }) }}
                className="w-5 h-5 rounded-full p-0"
                style={{
                  background: col,
                  border: (selected ? selected.color : color) === col ? '3px solid #111827' : '1px solid rgba(0,0,0,0.2)',
                }}
              />
            ))}
          </div>
          <button type="button" className={`${toolBtn(false)} ml-auto`} onClick={undo}>↩ Undo</button>
          <button type="button" className={toolBtn(false)} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            onClick={done}
            disabled={saving || !ready}
            className="px-3.5 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-bold hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Done'}
          </button>
        </div>

        {/* Selected-label editor — retype / resize / box / delete */}
        {selected && (
          <div className="flex items-center gap-1.5 flex-wrap p-1.5 rounded-lg bg-surface-secondary border border-border">
            <input
              ref={textInputRef}
              autoFocus
              value={selected.text}
              onChange={(e) => patch(selected.id, { text: e.target.value })}
              placeholder="Type your text…"
              className="flex-1 min-w-[140px] px-2 py-1 rounded-md text-sm font-semibold text-gray-900 bg-white outline-none"
              style={{ border: `2px solid ${selected.color}` }}
            />
            <label className="flex items-center gap-1 text-[11px] font-semibold text-txt-secondary">
              Size
              <input
                type="range"
                min={12}
                max={160}
                value={selected.size}
                onChange={(e) => patch(selected.id, { size: Number(e.target.value) })}
                className="w-24"
              />
            </label>
            <button type="button" className={toolBtn(selected.boxed)} onClick={() => patch(selected.id, { boxed: !selected.boxed })}>
              ▢ Box
            </button>
            <button
              type="button"
              onClick={removeSelected}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
            >
              🗑 Delete
            </button>
          </div>
        )}

        {/* Canvas */}
        <div className="relative inline-block">
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            className="block rounded-lg bg-black max-w-[92vw] max-h-[62vh]"
            style={{ touchAction: 'none', cursor: tool === 'text' ? 'text' : 'crosshair' }}
          />
        </div>

        <p className="text-[11px] text-txt-tertiary m-0">
          {tool === 'pen'
            ? 'Drag on the image to draw.'
            : 'Click the image to add text. Click a label to select it — then retype, resize, recolour, box it, or drag it.'}
        </p>
      </div>
    </div>,
    document.body,
  )
}
